import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import CreateProcessModal from '../components/CreateProcessModal';
import Toast from '../components/Toast';

import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  Clock,
  FlowArrow,
  Funnel,
  Lightning,
  MagnifyingGlass,
  Target,
  TrendUp,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const ProcessTemplates = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedComplexity, setSelectedComplexity] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForCreation, setTemplateForCreation] = useState(null);
  
  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'sales', label: 'Sales' },
    { value: 'support', label: 'Support' },
    { value: 'operations', label: 'Operations' },
    { value: 'finance', label: 'Finance' },
    { value: 'hr', label: 'HR' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'compliance', label: 'Compliance' }
  ];

  const complexityLevels = [
    { value: 'all', label: 'All Levels' },
    { value: 'simple', label: 'Simple' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'complex', label: 'Complex' }
  ];

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/process/templates`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = useCallback(() => {
    let filtered = [...templates];

    // MagnifyingGlass filter
    if (searchQuery) {
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Complexity filter
    if (selectedComplexity !== 'all') {
      filtered = filtered.filter(t => t.complexity === selectedComplexity);
    }

    setFilteredTemplates(filtered);
  }, [templates, searchQuery, selectedCategory, selectedComplexity]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [filterTemplates]);

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple':
        return 'text-[#14F195]';
      case 'moderate':
        return 'text-[#F59E0B]';
      case 'complex':
        return 'text-[#EC4899]';
      default:
        return 'text-[#A0AEC0]';
    }
  };

  const getCategoryIcon = (category) => {
    // Return appropriate icon based on category
    return FlowArrow;
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
        </div>
      
    );
  }

  return (
      <>
      <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <FlowArrow size={32} className="text-[#6366F1]" />
              <h1 className="text-4xl sm:text-5xl font-bold text-white">Process Templates</h1>
            </div>
            <p className="text-[#A0AEC0] text-base">
              Pre-built templates to quickly implement proven business processes with AI automation
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3"
            >
              <WarningCircle size={20} className="text-[#EF4444]" />
              <p className="text-[#EF4444] text-sm">{error}</p>
            </motion.div>
          )}

          {/* Filters */}
          <div className="glass rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Funnel size={20} className="text-[#6366F1]" />
              <h3 className="text-white font-bold">Filters</h3>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {/* MagnifyingGlass */}
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A0AEC0]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="w-full pl-10 pr-10 py-2 rounded-xl glass text-white placeholder-[#A0AEC0] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A0AEC0] hover:text-white"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Category Funnel */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>

              {/* Complexity Funnel */}
              <select
                value={selectedComplexity}
                onChange={(e) => setSelectedComplexity(e.target.value)}
                className="px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              >
                {complexityLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>

            {/* Active Filters Count */}
            {(searchQuery || selectedCategory !== 'all' || selectedComplexity !== 'all') && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-[#A0AEC0] text-sm">
                  {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} found
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                    setSelectedComplexity('all');
                  }}
                  className="text-[#6366F1] text-sm hover:text-[#4F46E5] transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Templates Grid or Empty State */}
          {filteredTemplates.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <FlowArrow size={64} className="mx-auto mb-6 text-[#A0AEC0]" />
              <h2 className="text-2xl font-bold text-white mb-4">No Templates Found</h2>
              <p className="text-[#A0AEC0] mb-8">
                Try adjusting your filters or search query
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedComplexity('all');
                }}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold hover:opacity-90 transition-opacity"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredTemplates.map((template) => (
                  <motion.div
                    key={template.template_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="glass rounded-2xl p-6 hover:bg-[rgba(255,255,255,0.08)] transition-all cursor-pointer group"
                    onClick={() => setSelectedTemplate(template)}
                  >
                    {/* Category Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 rounded-full glass text-xs font-semibold text-[#6366F1] border border-[rgba(99,102,241,0.3)]">
                        {template.category}
                      </span>
                      <span className={`text-xs font-semibold ${getComplexityColor(template.complexity)}`}>
                        {template.complexity}
                      </span>
                    </div>

                    {/* Template Name */}
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#6366F1] transition-colors">
                      {template.name}
                    </h3>

                    {/* Description */}
                    <p className="text-[#A0AEC0] text-sm mb-4 line-clamp-3">
                      {template.description}
                    </p>

                    {/* Key Metrics */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={14} className="text-[#6366F1]" />
                        <span className="text-[#A0AEC0]">Setup: {template.estimated_setup_time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <TrendUp size={14} className="text-[#14F195]" />
                        <span className="text-[#A0AEC0]">ROI: {template.estimated_roi_timeline}</span>
                      </div>
                    </div>

                    {/* Outcomes Preview */}
                    {template.typical_outcomes && template.typical_outcomes.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[#14F195] text-sm font-semibold mb-2">Key Outcomes:</p>
                        <p className="text-[#A0AEC0] text-xs line-clamp-2">
                          {template.typical_outcomes[0]}
                        </p>
                      </div>
                    )}

                    {/* View Details */}
                    <button className="w-full px-4 py-2 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold flex items-center justify-center gap-2 hover:scale-105 transition-transform">
                      View Details
                      <ArrowRight size={16} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

      {/* Template Detail Modal */}
      <AnimatePresence>
        {selectedTemplate && (
          <TemplateDetailModal
            template={selectedTemplate}
            onClose={() => setSelectedTemplate(null)}
            onInstantiate={(template) => {
              setTemplateForCreation(template);
              setSelectedTemplate(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Create Process Modal */}
      <AnimatePresence>
        {templateForCreation && (
          <CreateProcessModal
            template={templateForCreation}
            activeOrganization={activeOrganization}
            onClose={() => setTemplateForCreation(null)}
            onSuccess={(processId) => {
              setTemplateForCreation(null);
              setToast({
                isVisible: true,
                message: 'Process created from template successfully!',
                type: 'success'
              });
              // Redirect to the new process detail page
              setTimeout(() => {
                navigate(`/processes/${processId}`);
              }, 500);
            }}
          />
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
      </>
  );
};

// Template Detail Modal Component
const TemplateDetailModal = ({ template, onClose, onInstantiate }) => {
  
  const handleInstantiate = () => {
    onInstantiate(template);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.8)]"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="glass rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 glass rounded-t-2xl p-6 border-b border-[rgba(255,255,255,0.1)] flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 rounded-full glass text-xs font-semibold text-[#6366F1] border border-[rgba(99,102,241,0.3)]">
                {template.category}
              </span>
              <span className="text-xs font-semibold text-[#F59E0B]">
                {template.complexity}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">{template.name}</h2>
            <p className="text-[#A0AEC0]">{template.description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={18} className="text-[#6366F1]" />
                <span className="text-[#A0AEC0] text-sm">Setup Time</span>
              </div>
              <p className="text-white font-bold">{template.estimated_setup_time}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendUp size={18} className="text-[#14F195]" />
                <span className="text-[#A0AEC0] text-sm">ROI Timeline</span>
              </div>
              <p className="text-white font-bold">{template.estimated_roi_timeline}</p>
            </div>
          </div>

          {/* Typical Outcomes */}
          {template.typical_outcomes && template.typical_outcomes.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Target size={20} className="text-[#14F195]" />
                Typical Outcomes
              </h3>
              <ul className="space-y-2">
                {template.typical_outcomes.map((outcome, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#A0AEC0]">
                    <CheckCircle size={16} className="text-[#14F195] mt-0.5 flex-shrink-0" />
                    <span>{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Implementation Steps */}
          {template.steps && template.steps.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Lightning size={20} className="text-[#6366F1]" />
                Implementation Steps
              </h3>
              <div className="space-y-3">
                {template.steps.map((step, idx) => {
                  // Handle both string and object formats
                  const stepText = typeof step === 'string' ? step : (step.title || step.name || step.description || step.details || `Step ${idx + 1}`);
                  const stepDescription = typeof step === 'object' ? (step.description || step.details) : null;
                  
                  return (
                    <div key={idx} className="glass rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center text-white font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-white">{stepText}</p>
                          {stepDescription && (
                            <p className="text-[#A0AEC0] text-sm mt-1">{stepDescription}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Prerequisites */}
          {template.prerequisites && template.prerequisites.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3">Prerequisites</h3>
              <ul className="space-y-2">
                {template.prerequisites.map((prereq, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#A0AEC0]">
                    <span className="text-[#6366F1]">•</span>
                    <span>{prereq}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Success Metrics */}
          {template.success_metrics && template.success_metrics.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3">Success Metrics</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {template.success_metrics.map((metric, idx) => (
                  <div key={idx} className="glass rounded-xl p-3 flex items-center gap-2">
                    <TrendUp size={16} className="text-[#14F195]" />
                    <span className="text-[#A0AEC0] text-sm">{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Agent Types */}
          {template.recommended_agent_types && template.recommended_agent_types.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white mb-3">Recommended AI Agents</h3>
              <div className="flex flex-wrap gap-2">
                {template.recommended_agent_types.map((agentType, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 rounded-full glass text-sm text-[#6366F1] border border-[rgba(99,102,241,0.3)]"
                  >
                    {agentType}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 glass rounded-b-2xl p-6 border-t border-[rgba(255,255,255,0.1)] flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleInstantiate}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <FlowArrow size={20} />
            Instantiate Process
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProcessTemplates;
